import { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { ref, onValue, set, update, remove, get } from 'firebase/database';
import { auth as primaryAuth, db, firebaseConfig } from '../firebase';

const getCreateUserError = (code) => {
  const map = {
    'auth/email-already-in-use': 'このメールアドレスはすでに登録されています。',
    'auth/invalid-email': 'メールアドレスの形式が正しくありません。',
    'auth/weak-password': 'パスワードが弱すぎます。',
    'auth/operation-not-allowed': 'メール/パスワード認証が有効になっていません。',
  };
  return map[code] ?? 'ユーザーの作成に失敗しました。しばらく時間をおいて再試行してください。';
};

export const useAdminUsers = () => {
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ユーザー一覧の取得
  useEffect(() => {
    const usersRef = ref(db, 'users');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      setUsers(snapshot.val() || {});
      setLoading(false);
    }, (err) => {
      setError(err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 新規ユーザーの作成（サブアプリを使用して現在のログイン状態を保持）
  const createUser = async ({ email, displayName, role, plan = 'basic', contact = '', memo = '' }) => {
    setLoading(true);
    setError(null);
    try {
      // 1. サブインスタンスの作成
      const secondaryApp = initializeApp(firebaseConfig, `SecondaryApp_${Date.now()}`);
      const secondaryAuth = getAuth(secondaryApp);

      // 2. 一時的なランダムパスワードでAuthユーザーを作成
      const tempPassword = Math.random().toString(36).slice(-10) + 'A1!';
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, tempPassword);
      const uid = userCredential.user.uid;

      // 3. サブインスタンスからログアウト
      await signOut(secondaryAuth);

      // 4. DBにユーザー情報を保存
      const userData = {
        email,
        display_name: displayName,
        role: role.toLowerCase(),
        created_at: Date.now(),
        ...(contact.trim() && { contact: contact.trim() }),
        ...(memo.trim() && { memo: memo.trim() }),
        // ownerの場合はライセンス情報を付与（プランは指定値、デフォルトbasic）
        ...(role.toLowerCase() === 'owner' && {
          license: {
            plan: plan || 'basic',
            status: 'active',
            purchased_at: Date.now(),
            expires_at: Date.now() + 30 * 24 * 60 * 60 * 1000 // 30日後
          }
        })
      };
      
      await set(ref(db, `users/${uid}`), userData);

      // 5. パスワード再設定メール（招待メール）を送信
      await sendPasswordResetEmail(primaryAuth, email);

      return { success: true, uid };
    } catch (err) {
      console.error("Error creating user:", err);
      if (err.code === 'auth/email-already-in-use') {
        // DBで同一メールのユーザーを検索して既存情報を返す
        const snapshot = await get(ref(db, 'users'));
        const allUsers = snapshot.val() || {};
        const existing = Object.entries(allUsers).find(([, u]) => u.email === email);
        if (existing) {
          const [uid, userData] = existing;
          return { success: false, alreadyExists: true, existingUid: uid, existingUser: userData };
        }
        // AuthにはあるがDBにない場合 → 招待メール再送だけ行う
        try {
          await sendPasswordResetEmail(primaryAuth, email);
          return { success: false, authOnlyExists: true };
        } catch (_) {
          // 再送失敗は無視して下の汎用エラーへ
        }
      }
      const message = getCreateUserError(err.code);
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // ユーザー情報の更新（ライセンス等）
  const updateUser = async (uid, updates) => {
    setLoading(true);
    setError(null);
    try {
      await update(ref(db, `users/${uid}`), updates);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // ユーザーの削除（DBのみ）
  const deleteUser = async (uid) => {
    setLoading(true);
    setError(null);
    try {
      await remove(ref(db, `users/${uid}`));
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    deleteUser
  };
};
